import { ThemeOptions, createTheme } from "@material-ui/core";
import { ThemeName } from "../../common/types";
import { lightBlue, yellow } from "@material-ui/core/colors";
import { PaletteOptions } from "@material-ui/core/styles/createPalette";
import { merge } from "lodash";
import { FontStyleOptions } from "@material-ui/core/styles/createTypography";

interface GoogleMapStyle {
  /**
   * The element to which a styler should be applied. An element is a visual
   * aspect of a feature on the map. Example: a label, an icon, the stroke or
   * fill applied to the geometry, and more. Optional. If
   * <code>elementType</code> is not specified, the value is assumed to be
   * <code>'all'</code>. For details of usage and allowed values, see the <a
   * href="https://developers.google.com/maps/documentation/javascript/style-reference#style-elements">style
   * reference</a>.
   */
  elementType?: string | null;
  /**
   * The feature, or group of features, to which a styler should be applied.
   * Optional. If <code>featureType</code> is not specified, the value is
   * assumed to be <code>'all'</code>. For details of usage and allowed
   * values, see the <a
   * href="https://developers.google.com/maps/documentation/javascript/style-reference#style-features">style
   * reference</a>.
   */
  featureType?: string | null;
  /**
   * The style rules to apply to the selected map features and elements. The
   * rules are applied in the order that you specify in this array. For
   * guidelines on usage and allowed values, see the <a
   * href="https://developers.google.com/maps/documentation/javascript/style-reference#stylers">style
   * reference</a>.
   */
  stylers: object[];
}

interface CustomPalette {
  override: {
    MuiPapper: string;
  };
}

interface ThemeOverride {
  palette?: PaletteOptions;
  typography?: FontStyleOptions;
  googleMap?: Array<GoogleMapStyle>;
  customPalette?: CustomPalette;
  fontUrl?: string;
}

export interface ThemeOverrideWrapper {
  light?: ThemeOverride;
  dark?: ThemeOverride;}

const commonTheme = {
  palette: {
    primary: {
      main: lightBlue[500],
    },
  },
  typography: {
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
  },
};

const lightTheme = {
  ...commonTheme,
  palette: {
    primary: {
      main: lightBlue[500],
    },
    secondary: {
      light: "#3874cc",
      main: "#3874cc",
      dark: "#3874cc",
    },
    warning: {
      light: yellow[700],
      main: yellow[700],
      dark: yellow[700],
      contrastText: "#000000",
    },
  },
};

const darkTheme: Partial<ThemeOptions> = {
  ...commonTheme,
  palette: {
    primary: {
      main: "#90caf9",
    },
    secondary: {
      light: "#3874cc",
      main: "#3874cc",
      dark: "#3874cc",
    },
    warning: yellow,
    type: "dark",
  },
};

export const createProLogsTheme = (
  themeName: ThemeName,
  themeOverride: PaletteOptions,
  fontOverride: FontStyleOptions,
  customPalette?: CustomPalette,
) => {
  const theme =
    themeName === "dark"
      ? merge(darkTheme, {
          palette: { ...themeOverride },
          typography: { ...fontOverride },
          customPalette: customPalette,
        })
      : merge(lightTheme, {
          palette: { ...themeOverride },
          typography: { ...fontOverride },
          customPalette: customPalette,
        });

  return createTheme(theme);
};
